import React, { useState } from 'react';
import { Brain, Sparkles, AlertTriangle, TrendingUp, TrendingDown, Minus, RefreshCw, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface AIAnalysisProps {
  symbol: string;
  onAnalysisComplete?: (analysis: AIAnalysisResult) => void;
}

interface AIAnalysisResult {
  symbol: string;
  analysis_type: string;
  generated_at: string;
  recommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  confidence: number;
  summary: string;
  analysis: string;
  key_points: string[];
  risks: string[];
  opportunities?: string[];
  technical_outlook?: string;
  fundamental_outlook?: string;
  provider?: string;
  model_used?: string;
}

const AIAnalysis: React.FC<AIAnalysisProps> = ({ symbol, onAnalysisComplete }) => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AIAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analysisType, setAnalysisType] = useState<'comprehensive' | 'technical' | 'fundamental' | 'sentiment'>('comprehensive');
  const [additionalContext, setAdditionalContext] = useState<string>('');

  const runAnalysis = async () => {
    if (!isAuthenticated) {
      setError('Please sign in to use AI analysis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Auto-populate context with current price if user hasn't provided context
      let contextToSend = additionalContext;
      if (!additionalContext || additionalContext.trim() === '') {
        try {
          // Fetch current price data
          const quote = await api.getRealTimePrice(symbol);
          const changeSign = (quote.change_percent ?? quote.change_p ?? 0) >= 0 ? '+' : '';
          contextToSend = `Current price: ${quote.close?.toFixed(2) || 'N/A'}, Change: ${changeSign}${(quote.change_percent ?? quote.change_p ?? 0).toFixed(2)}%`;

          // Add previous close if available
          if (quote.previousClose ?? quote.previous_close) {
            contextToSend += `, Previous close: ${(quote.previousClose ?? quote.previous_close)?.toFixed(2)}`;
          }
        } catch (priceErr) {
          console.warn('Could not fetch price for auto-context:', priceErr);
          // Continue without price context if fetch fails
        }
      }

      const result = await api.getAIAnalysis(symbol, analysisType, contextToSend || undefined);
      setAnalysis(result as unknown as AIAnalysisResult);
      onAnalysisComplete?.(result as unknown as AIAnalysisResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = (rec: string) => {
    switch (rec) {
      case 'strong_buy':
        return 'text-emerald-400 bg-emerald-500/20';
      case 'buy':
        return 'text-green-400 bg-green-500/20';
      case 'hold':
        return 'text-amber-400 bg-amber-500/20';
      case 'sell':
        return 'text-orange-400 bg-orange-500/20';
      case 'strong_sell':
        return 'text-red-400 bg-red-500/20';
      default:
        return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getRecommendationIcon = (rec: string) => {
    switch (rec) {
      case 'strong_buy':
      case 'buy':
        return <TrendingUp size={20} />;
      case 'hold':
        return <Minus size={20} />;
      case 'sell':
      case 'strong_sell':
        return <TrendingDown size={20} />;
      default:
        return <Minus size={20} />;
    }
  };

  const formatRecommendation = (rec: string) => {
    return rec.replace('_', ' ').toUpperCase();
  };

  if (!isAuthenticated) {
    return (
      <Card title="AI Analysis">
        <div className="text-center py-8">
          <Brain size={40} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">Sign in to access AI-powered investment analysis</p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div className="flex items-center gap-2">
          <Brain className="text-purple-400" size={20} />
          <span>AI Analysis</span>
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-purple-500/20 text-purple-400 border-purple-500/30 font-medium">
            <Brain size={11} />
            AI
          </span>
        </div>
      }
    >
      {/* Analysis Type Selector */}
      <div className="flex gap-2 mb-4">
        {(['comprehensive', 'technical', 'fundamental', 'sentiment'] as const).map((type) => (
          <button
            key={type}
            onClick={() => setAnalysisType(type)}
            className={`px-3 py-1.5 text-xs rounded-lg capitalize transition-colors ${
              analysisType === type
                ? 'bg-purple-500 text-white'
                : 'bg-slate-700 text-slate-400 hover:text-white'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Additional Context Input */}
      {!analysis && !loading && (
        <div className="mb-4">
          <label htmlFor="additional-context" className="block text-sm font-medium text-slate-300 mb-2">
            Additional Context (Optional)
          </label>
          <textarea
            id="additional-context"
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
            placeholder="e.g., AGL.JSE has reached a price of 703.45 which is the resistance"
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
            rows={3}
          />
          <p className="text-xs text-slate-500 mt-1">
            Provide context like resistance/support levels, price targets, or market observations
          </p>
        </div>
      )}

      {/* Run Analysis Button */}
      {!analysis && !loading && (
        <button
          onClick={runAnalysis}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-semibold rounded-lg transition-all"
        >
          <Sparkles size={18} />
          Run AI Analysis
        </button>
      )}

      {/* Loading State */}
      {loading && (
        <div className="py-8 text-center">
          <LoadingSpinner />
          <p className="text-sm text-slate-400 mt-4">Analyzing {symbol}...</p>
          <p className="text-xs text-slate-500 mt-1">This may take a few seconds</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 bg-red-400/10 px-4 py-3 rounded-lg text-sm mt-4">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Analysis Results */}
      {analysis && !loading && (
        <div className="space-y-4 mt-4">
          {/* Recommendation */}
          <div className="text-center py-4">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${getRecommendationColor(
                analysis.recommendation
              )}`}
            >
              {getRecommendationIcon(analysis.recommendation)}
              <span className="text-xl font-bold">
                {formatRecommendation(analysis.recommendation)}
              </span>
            </div>
            <div className="mt-2 text-sm text-slate-400">
              Confidence: {(analysis.confidence * 100).toFixed(0)}%
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-700/30 rounded-lg p-4">
            <p className="text-sm">{analysis.summary}</p>
          </div>

          {/* Key Points */}
          {analysis.key_points && analysis.key_points.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-300 mb-2">Key Points</h4>
              <ul className="space-y-1">
                {analysis.key_points.map((point, i) => (
                  <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                    <span className="text-teal-400">•</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risks */}
          {analysis.risks && analysis.risks.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-1">
                <AlertTriangle size={14} />
                Risks
              </h4>
              <ul className="space-y-1">
                {analysis.risks.map((risk, i) => (
                  <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                    <span className="text-red-400">•</span>
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Opportunities */}
          {analysis.opportunities && analysis.opportunities.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-emerald-400 mb-2">Opportunities</h4>
              <ul className="space-y-1">
                {analysis.opportunities.map((opp, i) => (
                  <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                    <span className="text-emerald-400">•</span>
                    {opp}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Technical/Fundamental Outlook */}
          {(analysis.technical_outlook || analysis.fundamental_outlook) && (
            <div className="grid grid-cols-2 gap-4 text-xs">
              {analysis.technical_outlook && (
                <div className="bg-slate-700/30 rounded-lg p-3">
                  <h5 className="font-semibold text-slate-300 mb-1">Technical</h5>
                  <p className="text-slate-400">{analysis.technical_outlook}</p>
                </div>
              )}
              {analysis.fundamental_outlook && (
                <div className="bg-slate-700/30 rounded-lg p-3">
                  <h5 className="font-semibold text-slate-300 mb-1">Fundamental</h5>
                  <p className="text-slate-400">{analysis.fundamental_outlook}</p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setAnalysis(null)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-sm"
            >
              <RefreshCw size={14} />
              Run New Analysis
            </button>
            <Link
              to={`/ai-history?symbol=${symbol}`}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg transition-colors text-sm text-purple-400"
            >
              <History size={14} />
              View History
            </Link>
          </div>

          {/* Timestamp */}
          <p className="text-xs text-slate-500 text-center">
            Generated: {new Date(analysis.generated_at).toLocaleString()}
          </p>
        </div>
      )}
    </Card>
  );
};

export default AIAnalysis;
